import { getSupabaseClient } from "../providers/supabase/supabase";
import type { GulaClient } from "./gulaTypes";

/**
 * Lead trash: admins throw away "leads" that are not real leads (team mates,
 * installers, suppliers writing to the WhatsApp number). Keep in sync with
 * gula_lead_trashable() in 20261103230000_gula_lead_trash.sql.
 */
export const TRASHABLE_SOURCES = [
  "whatsapp",
  "instagram",
  "facebook",
  "manual",
];

const PROTECTED_STAGES = ["sure_purchase", "purchased"];

export type TrashableLead = Pick<
  GulaClient,
  "source" | "form_name" | "external_lead_id" | "stage" | "order_stage"
>;

/** Direct-message or hand-made leads, without a purchase. Form leads never. */
export const isTrashable = (client: TrashableLead): boolean =>
  TRASHABLE_SOURCES.includes(client.source ?? "") &&
  !client.form_name &&
  !client.external_lead_id &&
  !PROTECTED_STAGES.includes(client.stage) &&
  !client.order_stage;

export const SOURCE_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  manual: "a mano",
};

export interface TrashedLead {
  id: number;
  client_id: number;
  contact_id: number | null;
  name: string | null;
  phone: string | null;
  source: string | null;
  stage: string | null;
  trashed_by: number | null;
  trashed_at: string;
}

const rpcError = (error: { message: string } | null) => {
  if (error) throw new Error(error.message);
};

export const trashLead = async (client: GulaClient): Promise<void> => {
  const { error } = await getSupabaseClient().rpc("gula_lead_to_trash", {
    p_client_id: Number(client.id),
  });
  rpcError(error);
};

export const restoreLead = async (trashId: number): Promise<void> => {
  const { error } = await getSupabaseClient().rpc("gula_lead_restore", {
    p_trash_id: trashId,
  });
  rpcError(error);
};

export const listTrash = async (): Promise<TrashedLead[]> => {
  const { data, error } = await getSupabaseClient()
    .from("gula_lead_trash")
    .select(
      "id, client_id, contact_id, name, phone, source, stage, trashed_by, trashed_at",
    )
    .is("restored_at", null)
    .order("trashed_at", { ascending: false })
    .limit(200);
  rpcError(error);
  return (data ?? []) as TrashedLead[];
};
